/**
 * Keeps Project Hub (timber projects), portal timeline params, and sales orders in sync
 * when a deal reaches "signed" (client signature or internal portal advance to quote-signed).
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  query,
  where,
  addDoc,
  deleteDoc,
  updateDoc,
  Timestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import { getSettings } from './appSettings';
import { missingContractedTaskGroups } from './ensureContractedTaskGroups';
import { normalizeOnboardingDepartment } from './onboardingTaskBridge';
import { DEFAULT_CONTRACTED_TASK_GROUPS } from './taskGroupOrder';
import { speciesTimberKitBillable } from './proposalSpeciesProduction';
import {
  computeProposalGrandTotals,
  calculateProposalNodeBucketsWithSelections,
  getClientVisibleNodeSets,
  parseStructureSelectionsForPublicTotals,
  parseSpeciesSelectionsForPublicTotals,
} from './proposalPublicGrandTotals';
import type { EnhancedDeal, ProposalBuilderData, SalesOrderLineItem, TimberProject } from '../types';
import { pickWonPipelineStageId } from './pipelineStageUtils';
import { shouldPromoteTimberPhaseToContractedOnSign } from './proposalSignProjectPhase';
import {
  parseSimulatedSchedules,
  STAFF_PLANNING_COLLECTION,
  STAFF_PLANNING_HUB_DOC_ID,
  withSimulatedSchedule,
} from './simulateUnderContract';
import { triggerQuickBooksInvoiceForSalesOrder } from './quickbooksInvoiceTrigger';
import { removeUndefinedDeep } from './removeUndefinedDeep';
import { recordPaymentForecastSnapshotForDeal } from './paymentForecastLog';

/** Firestore proposal docs include signing-time fields beyond `ProposalBuilderData`. */
type ProposalBuilderFirestoreSnapshot = Omit<
  Partial<ProposalBuilderData>,
  'speciesPricing' | 'selectedSpecies'
> & {
  installSelection?: boolean;
  speciesPricing?: {
    speciesName?: string;
    timberTotal?: number;
    billableTotal?: number;
    selections?: Record<string, string>;
  };
  /** Per-structure species after sign, or legacy single string */
  selectedSpecies?: Record<string, string> | string;
};

export type SignedDealSyncDeps = {
  convertDealToProject: (deal: EnhancedDeal, userId: string, frameType?: 'custom' | 'kit') => Promise<string>;
  markDealWon: (dealId: string, userId: string) => Promise<void>;
  updateTimberProject: (id: string, updates: Partial<TimberProject>) => Promise<void>;
};

const DEFAULT_TIMELINE_PHASES = [
  { id: '2d-plans', label: 'Engineered Drawings', durationWeeks: 3, bufferWeeks: 1 },
  {
    id: 'in-production',
    label: 'Production',
    durationWeeks: 8,
    bufferWeeks: 1,
    subPhases: [
      { id: 'material-lead', label: 'Material Lead Time', durationWeeks: 6 },
      { id: 'cnc-production', label: 'CNC Production', durationWeeks: 2 },
    ],
  },
  { id: 'delivery', label: 'Delivery & Install', durationWeeks: 2, bufferWeeks: 0 },
];

async function resolveTimelineParamsForDeal(dealId: string): Promise<typeof DEFAULT_TIMELINE_PHASES> {
  let resolved = DEFAULT_TIMELINE_PHASES;
  try {
    const byDeal = await getDocs(query(collection(db, 'clientPortals'), where('dealId', '==', dealId)));
    if (!byDeal.empty) {
      const pData = byDeal.docs[0].data();
      const projData = pData.projects?.[dealId];
      if (projData?.timelineOverrides?.length) {
        resolved = projData.timelineOverrides;
      }
    }
  } catch {
    /* ignore */
  }
  if (resolved === DEFAULT_TIMELINE_PHASES) {
    try {
      const settings = await getSettings();
      if (settings.projectTimelineDefaults?.phases?.length) {
        resolved = settings.projectTimelineDefaults.phases as typeof DEFAULT_TIMELINE_PHASES;
      }
    } catch {
      /* ignore */
    }
  }
  return resolved;
}

async function ensureContractedTaskGroups(projectId: string): Promise<void> {
  const snap = await getDocs(query(collection(db, 'taskGroups'), where('projectId', '==', projectId)));
  const existing = snap.docs.map((d) => {
    const data = d.data() as { name?: string; position?: number };
    return {
      id: d.id,
      projectId,
      name: String(data.name || ''),
      position: data.position || 0,
    };
  });
  for (const g of missingContractedTaskGroups(projectId, existing)) {
    await addDoc(collection(db, 'taskGroups'), {
      projectId,
      name: g.name,
      color: g.color,
      position: g.position,
    });
  }
}

async function generateOnboardingChecklist(projectId: string, projectName: string): Promise<void> {
  try {
    const existing = await getDoc(doc(db, 'projectOnboarding', projectId));
    if (existing.exists()) return;

    const templateSnap = await getDoc(doc(db, 'settings', 'onboardingChecklist'));
    if (!templateSnap.exists()) return;
    const template = templateSnap.data();
    if (!template.items?.length) return;

    const userCache: Record<string, string> = {};
    for (const item of template.items) {
      if (item.defaultAssigneeId && !userCache[item.defaultAssigneeId]) {
        try {
          const uSnap = await getDoc(doc(db, 'users', item.defaultAssigneeId));
          if (uSnap.exists()) {
            const u = uSnap.data();
            userCache[item.defaultAssigneeId] = u.displayName || u.email || '';
          }
        } catch { /* skip */ }
      }
    }

    let simulatedByItem: ReturnType<typeof parseSimulatedSchedules>[string] | undefined;
    try {
      const planningSnap = await getDoc(doc(db, STAFF_PLANNING_COLLECTION, STAFF_PLANNING_HUB_DOC_ID));
      simulatedByItem = parseSimulatedSchedules(planningSnap.data())[projectId];
    } catch {
      /* Simulated dates are optional; the checklist still generates. */
    }

    const items = template.items.map((t: any) => withSimulatedSchedule({
      id: t.id,
      title: t.title,
      description: t.description || '',
      category: t.category,
      department: normalizeOnboardingDepartment(t.department),
      assigneeId: t.defaultAssigneeId || null,
      assigneeName: t.defaultAssigneeId ? (userCache[t.defaultAssigneeId] || null) : null,
      required: t.required ?? true,
      order: t.order,
      completed: false,
      completedAt: null,
      completedBy: null,
      completedByName: null,
      scheduleNeedsPmReview: true,
    }, simulatedByItem));

    await setDoc(doc(db, 'projectOnboarding', projectId), {
      projectId,
      projectName,
      templateVersion: template.updatedAt || Timestamp.now(),
      status: 'active',
      items,
      createdAt: Timestamp.now(),
    });
  } catch (err) {
    console.error('[signedDealSync] Error generating onboarding checklist:', err);
  }
}

export interface EnsureBaseProposalSalesOrderOptions {
  proposalDocId?: string;
}

/** Billable line items — matches PublicProposalPage signing (uses billablePrices when signed). */
function buildBaseProposalSalesOrderLineItems(
  proposal: ProposalBuilderData,
  nodeSets: ProposalBuilderData['nodeSets'],
  structureSelections: Record<string, boolean> | undefined,
  selectedSpecies: Record<string, string>,
  includeInstall: boolean,
  margin: number,
): SalesOrderLineItem[] {
  return nodeSets.map(ns => {
    const buckets = calculateProposalNodeBucketsWithSelections(
      ns,
      margin,
      proposal,
      structureSelections,
    );
    const speciesName =
      selectedSpecies[ns.id] ||
      (proposal.speciesPricing as { selections?: Record<string, string> } | undefined)?.selections?.[ns.id] ||
      'Default';

    const adjustedTimberKit = speciesTimberKitBillable(
      ns,
      selectedSpecies[ns.id],
      buckets.timberKit,
      margin,
      ns.quantity || 1,
      ns.billablePrices?.speciesOptions,
    );

    return {
      structureId: ns.id,
      structureName: ns.name || 'Structure',
      quantity: ns.quantity || 1,
      species: speciesName,
      designCost: buckets.design,
      timberKitCost: adjustedTimberKit,
      installCost: includeInstall ? buckets.install : 0,
      subtotal: buckets.design + adjustedTimberKit + (includeInstall ? buckets.install : 0),
    };
  });
}

/**
 * Create the original signed-proposal sales order for a deal (not change orders).
 * Idempotent — skips when a base sales order already exists; change-order orders
 * do not count. Also backfills a missing QuickBooks invoice on an existing base order.
 */
export function baseProposalSalesOrderDocId(dealId: string): string {
  return `so-base-${dealId}`;
}

export async function ensureBaseProposalSalesOrder(
  dealId: string,
  options?: EnsureBaseProposalSalesOrderOptions,
): Promise<string | null> {
  const proposalDocId = options?.proposalDocId ?? dealId;

  const soq = await getDocs(query(collection(db, 'salesOrders'), where('dealId', '==', dealId)));
  const baseDoc = soq.docs.find(d => !d.data().isChangeOrder);
  if (baseDoc) {
    const data = baseDoc.data();
    // Do not rewrite legacy lump-sum orders to progress — that reprojects
    // already-billed contracts as unpaid installments on the forecast.
    if (!data.quickbooksInvoiceId && data.billingStyle === 'progress') {
      triggerQuickBooksInvoiceForSalesOrder(baseDoc.id, 'deposit').catch(err =>
        console.warn('[signedDealSync] QB invoice backfill failed', err),
      );
    }
    return baseDoc.id;
  }

  const propSnap = await getDoc(doc(db, 'proposalBuilders', proposalDocId));
  if (!propSnap.exists()) return null;

  const proposal = propSnap.data() as ProposalBuilderFirestoreSnapshot;
  const proposalFull = proposal as ProposalBuilderData;
  const structureSelections = parseStructureSelectionsForPublicTotals(proposalFull);
  const nodeSets = getClientVisibleNodeSets(proposalFull, structureSelections);
  if (nodeSets.length === 0) return null;

  const margin = typeof proposal.profitMargin === 'number' ? proposal.profitMargin : 0.25;
  const { selectedSpecies } = parseSpeciesSelectionsForPublicTotals(proposalFull);
  const includeInstall = proposal.installOptional ? proposal.installSelection !== false : true;
  const grandTotals = computeProposalGrandTotals(proposalFull, {
    includeInstall,
    selectedSpecies,
    structureSelections,
  });
  const lineItems = buildBaseProposalSalesOrderLineItems(
    proposalFull,
    nodeSets,
    structureSelections,
    selectedSpecies,
    includeInstall,
    margin,
  );

  // Guard against parallel backfills (Won Deals + Change Orders) creating duplicates.
  const recheck = await getDocs(query(collection(db, 'salesOrders'), where('dealId', '==', dealId)));
  const racedBase = recheck.docs.find(d => !d.data().isChangeOrder);
  if (racedBase) {
    if (!racedBase.data().quickbooksInvoiceId) {
      const backfillInstallment = racedBase.data().billingStyle === 'progress' ? 'deposit' : undefined;
      triggerQuickBooksInvoiceForSalesOrder(racedBase.id, backfillInstallment).catch(err =>
        console.warn('[signedDealSync] QB invoice backfill failed', err),
      );
    }
    return racedBase.id;
  }

  const deterministicId = baseProposalSalesOrderDocId(dealId);
  await setDoc(
    doc(db, 'salesOrders', deterministicId),
    removeUndefinedDeep({
      proposalId: proposalDocId,
      dealId,
      clientName: proposal.clientName || 'Unknown',
      signedAt: Timestamp.now(),
      signedBy: proposal.signature?.signedBy || 'Customer',
      signatureDataUrl: proposal.signature?.dataUrl || '',
      includeInstall,
      selectedSpecies:
        proposal.speciesPricing?.selections ||
        (typeof proposal.selectedSpecies === 'object' && proposal.selectedSpecies !== null
          ? (proposal.selectedSpecies as Record<string, string>)
          : {}) ||
        {},
      ...(structureSelections ? { structureSelections } : {}),
      designTotal: grandTotals.design,
      timberKitTotal: grandTotals.timberKit,
      installTotal: includeInstall ? grandTotals.install : 0,
      grandTotal: grandTotals.total,
      profitMargin: margin,
      lineItems,
      // Progress billing: full grandTotal is the sales-volume snapshot, but QB is
      // billed one installment at a time (50% deposit at signing, then 40%/10%).
      billingStyle: 'progress',
      createdAt: Timestamp.now(),
    }),
  );

  // onSalesOrderCreated creates the QB invoice (deposit only for progress orders)
  // — do not double-trigger from the client.
  return deterministicId;
}

/**
 * Delete erroneous base (non-CO) sales orders and recreate from the signed proposal.
 * Use when backfill created cost-priced or duplicate orders. Does not void QB invoices.
 */
/**
 * Idempotent heal for won deals: progress sales order + Under Contract Hub phase
 * so Billing & Payables and the finances forecast can see installments.
 */
export async function ensureSignedDealBillingReady(
  dealId: string,
  options?: { proposalDocId?: string; actorId?: string },
): Promise<void> {
  await syncSignedDealSideEffects(dealId, options?.actorId || 'billing-heal', {
    skipMarkWon: true,
    skipSalesOrder: false,
    proposalDocId: options?.proposalDocId,
  });
}

export async function recreateBaseProposalSalesOrder(
  dealId: string,
  options?: EnsureBaseProposalSalesOrderOptions,
): Promise<string | null> {
  const soq = await getDocs(query(collection(db, 'salesOrders'), where('dealId', '==', dealId)));
  await Promise.all(
    soq.docs.filter(d => !d.data().isChangeOrder).map(d => deleteDoc(d.ref)),
  );
  return ensureBaseProposalSalesOrder(dealId, options);
}

/**
 * @param options.skipMarkWon — when deal was already marked won (e.g. public proposal flow)
 * @param options.skipSalesOrder — when sales order was already written (e.g. public proposal flow)
 * @param options.proposalDocId — Firestore `proposalBuilders` document id when it differs from `dealId` (legacy layouts)
 * @param options.deps — required for full behavior when running inside CRM (early-funnel → Under Contract, convertDealToProject).
 *                       When omitted, only Firestore fallbacks run for timber updates (public route).
 */
export async function syncSignedDealSideEffects(
  dealId: string,
  userId: string,
  options?: {
    skipMarkWon?: boolean;
    skipSalesOrder?: boolean;
    proposalDocId?: string;
    deps?: SignedDealSyncDeps;
  },
): Promise<void> {
  const { skipMarkWon, skipSalesOrder, deps } = options || {};
  const proposalDocId = options?.proposalDocId ?? dealId;

  const dealSnap = await getDoc(doc(db, 'enhancedDeals', dealId));
  if (!dealSnap.exists()) return;

  // Legacy: mark sent/opened proposals approved when sync runs (client sign sets this first).
  try {
    const proposalSnap = await getDoc(doc(db, 'proposalBuilders', proposalDocId));
    if (proposalSnap.exists()) {
      const proposalStatus = proposalSnap.data().status as string | undefined;
      if (proposalStatus === 'sent' || proposalStatus === 'opened') {
        await updateDoc(doc(db, 'proposalBuilders', proposalDocId), {
          status: 'approved',
          approvedAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
        });
      }
    }
  } catch (err) {
    console.error('[signedDealSync] Error updating proposal status:', err);
  }

  let deal = { id: dealId, ...dealSnap.data() } as EnhancedDeal;

  if (!skipMarkWon && deal.outcome !== 'won') {
    if (deps?.markDealWon) {
      await deps.markDealWon(dealId, userId);
    } else {
      const now = new Date();
      const d = dealSnap.data();
      let wonStageId = (d.stageId as string) || 'won';
      const pipelineId = d.pipelineId as string | undefined;
      if (pipelineId) {
        try {
          const ps = await getDoc(doc(db, 'pipelines', pipelineId));
          if (ps.exists()) {
            const stages = (ps.data().stages || []) as Array<{ id: string; name: string; order: number }>;
            const picked = pickWonPipelineStageId(stages);
            if (picked) wonStageId = picked;
          }
        } catch {
          /* keep wonStageId */
        }
      }
      const wonTransition = {
        fromStage: d.stageId,
        toStage: wonStageId,
        transitionedAt: Timestamp.fromDate(now),
        durationMs: Math.max(
          0,
          now.getTime() - new Date(d.currentStageEnteredAt?.toDate?.() ?? d.createdAt?.toDate?.() ?? now).getTime(),
        ),
        transitionedBy: userId,
      };
      const wonAt = Timestamp.fromDate(now);
      await updateDoc(doc(db, 'enhancedDeals', dealId), {
        outcome: 'won',
        stageId: wonStageId,
        closedAt: wonAt,
        ...(d.firstWonAt ? {} : { firstWonAt: wonAt }),
        probability: 100,
        currentStageEnteredAt: Timestamp.fromDate(now),
        stageHistory: [...(d.stageHistory || []), wonTransition],
        updatedAt: Timestamp.now(),
      });
    }
    const fresh = await getDoc(doc(db, 'enhancedDeals', dealId));
    deal = { id: dealId, ...fresh.data() } as EnhancedDeal;
  }

  const resolvedPhases = await resolveTimelineParamsForDeal(dealId);

  const tq = await getDocs(query(collection(db, 'timberProjects'), where('dealId', '==', dealId)));

  if (tq.empty) {
    if (deps?.convertDealToProject && deal.outcome === 'won') {
      try {
        await deps.convertDealToProject(deal, userId, 'custom');
        const tq2 = await getDocs(query(collection(db, 'timberProjects'), where('dealId', '==', dealId)));
        for (const d of tq2.docs) {
          await deps.updateTimberProject(d.id, { timelineParams: resolvedPhases });
          await generateOnboardingChecklist(d.id, d.data().name || deal.title || 'Project');
        }
      } catch (err) {
        console.error('[signedDealSync] convertDealToProject failed:', err);
      }
    } else if (deal.outcome === 'won') {
      // Fallback: create contracted project directly when CRM deps aren't available
      // (e.g. public proposal signing path)
      try {
        const now = new Date();
        const targetEnd = new Date(now);
        targetEnd.setMonth(targetEnd.getMonth() + 6);
        const projectData: Record<string, any> = {
          dealId: deal.id!,
          clientEmail: '',
          clientName: deal.contactName || '',
          name: deal.title || 'Untitled Project',
          description: deal.description || '',
          status: 'planning',
          frameType: 'custom',
          phase: 'contracted',
          contractedAt: Timestamp.fromDate(now),
          startDate: Timestamp.fromDate(now),
          targetEndDate: Timestamp.fromDate(targetEnd),
          value: deal.value || 0,
          location: deal.location || '',
          projectType: deal.projectType || '',
          projectManager: userId,
          createdAt: Timestamp.fromDate(now),
          updatedAt: Timestamp.fromDate(now),
          timelineParams: resolvedPhases,
        };
        if (deal.address) projectData.address = deal.address;
        if (deal.coordinates) projectData.coordinates = deal.coordinates;

        const projRef = await addDoc(collection(db, 'timberProjects'), projectData);

        for (let i = 0; i < DEFAULT_CONTRACTED_TASK_GROUPS.length; i++) {
          const g = DEFAULT_CONTRACTED_TASK_GROUPS[i];
          await addDoc(collection(db, 'taskGroups'), {
            projectId: projRef.id,
            name: g.name,
            color: g.color,
            position: i + 1,
          });
        }

        await generateOnboardingChecklist(projRef.id, deal.title || 'Project');
        console.log(`[signedDealSync] Created contracted project ${projRef.id} for deal ${dealId} (no CRM deps)`);
      } catch (err) {
        console.error('[signedDealSync] Fallback project creation failed:', err);
      }
    } else {
      console.warn(
        '[signedDealSync] No timber project and deal not won — skipping project creation.',
      );
    }
  } else {
    for (const d of tq.docs) {
      const pdata = d.data();
      const updates: Record<string, unknown> = {
        timelineParams: resolvedPhases,
        updatedAt: Timestamp.now(),
      };
      if (shouldPromoteTimberPhaseToContractedOnSign(pdata.phase)) {
        updates.phase = 'contracted';
        updates.contractedAt = Timestamp.now();
        await ensureContractedTaskGroups(d.id);
        await generateOnboardingChecklist(d.id, pdata.name || deal.title || 'Project');
      }
      if (deps?.updateTimberProject) {
        await deps.updateTimberProject(d.id, updates as Partial<TimberProject>);
      } else {
        await updateDoc(doc(db, 'timberProjects', d.id), updates as Record<string, any>);
      }
    }
  }

  // Initial payment forecast snapshot (best-effort — staff-only rules mean the
  // public anonymous signing path may be denied; the first staff milestone
  // touch will then seed the log instead).
  void recordPaymentForecastSnapshotForDeal(dealId, 'schedule_created', { byUid: userId });

  if (skipSalesOrder) return;

  await ensureBaseProposalSalesOrder(dealId, { proposalDocId });
}
